import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import dayjs from 'shared/helpers/dayjs';
import { Event } from 'events/_types';
import { Note } from 'shiftNotes/_types';
import getCreatedNoteLocation from 'events/helpers/createdNoteLocation';

interface UseNoteCaptureNavigationOptions {
  events: Event[];
  isDesktopView: boolean;
  selectedDate: dayjs.Dayjs;
  setSummaryExpanded: (next: boolean) => void;
  setEditingPreferences: (next: boolean) => void;
  setShowPriorities: (next: boolean) => void;
}

interface UseNoteCaptureNavigationResult {
  handleNotesCreated: (notes: Note[]) => void;
}

export default function useNoteCaptureNavigation({
  events,
  isDesktopView,
  selectedDate,
  setSummaryExpanded,
  setEditingPreferences,
  setShowPriorities,
}: UseNoteCaptureNavigationOptions): UseNoteCaptureNavigationResult {
  const history = useHistory();
  const snapshotEventIdsRef = useRef<Set<string> | null>(null);
  const createdNoteIdsRef = useRef<string[] | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!isPending) return;
    if (!isDesktopView) {
      setIsPending(false);
      return;
    }

    const snapshot = snapshotEventIdsRef.current;
    if (!snapshot) {
      setIsPending(false);
      return;
    }

    const createdNoteIds = createdNoteIdsRef.current ?? [];
    const containingEvent = createdNoteIds.length
      ? events.find((e) =>
          (e.shift_notes ?? []).some((n) => createdNoteIds.includes(n.id))
        )
      : undefined;

    let targetEventId: string;
    if (containingEvent) {
      targetEventId = containingEvent.id;
    } else {
      const newEvents = events.filter((e) => !snapshot.has(e.id));
      if (newEvents.length === 0) return;
      const newestEvent = [...newEvents].sort(
        (a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf()
      )[0];
      targetEventId = newestEvent.id;
    }

    setSummaryExpanded(false);
    setEditingPreferences(false);
    setShowPriorities(false);
    history.push(getCreatedNoteLocation(targetEventId, selectedDate));
    setIsPending(false);
  }, [
    isPending,
    events,
    isDesktopView,
    selectedDate,
    history,
    setSummaryExpanded,
    setEditingPreferences,
    setShowPriorities,
  ]);

  const handleNotesCreated = useCallback(
    (notes: Note[]) => {
      if (!isDesktopView) return;
      snapshotEventIdsRef.current = new Set(events.map((e) => e.id));
      createdNoteIdsRef.current = notes.map((n) => n.id);
      setIsPending(true);
    },
    [events, isDesktopView]
  );

  return {
    handleNotesCreated,
  };
}
